import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./common.css";

const TYPING_LINES = [
  "INITIALIZING SECURE SESSION...",
  "ENCRYPTING CHANNEL............",
  "NAPPS BANK v3.1.4 READY.......",
];

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bootLine, setBootLine] = useState(0);
  const [bootDone, setBootDone] = useState(false);
  const [shake, setShake] = useState(false);
  const [focusField, setFocusField] = useState(null);
  const nav = useNavigate();
  const cardRef = useRef(null);

  // Boot sequence
  useEffect(() => {
    if (bootLine < TYPING_LINES.length) {
      const t = setTimeout(() => setBootLine((l) => l + 1), 600);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => setBootDone(true), 300);
      return () => clearTimeout(t);
    }
  }, [bootLine]);

  // 3-D tilt on mouse move
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const handleMove = (e) => {
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      card.style.transform = `perspective(900px) rotateY(${dx * 6}deg) rotateX(${-dy * 6}deg) translateY(-4px)`;
    };
    const handleLeave = () => {
      card.style.transform = "perspective(900px) rotateY(0deg) rotateX(0deg) translateY(0px)";
    };
    card.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", handleLeave);
    return () => {
      card.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", handleLeave);
    };
  }, [bootDone]);

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const login = async () => {
    if (!username || !password) {
      setError("ALL FIELDS REQUIRED");
      triggerShake();
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await axios.post("https://atmsim-pip.onrender.com/api/login", { username, password });
      if (res.data.token) {
        localStorage.setItem("token", res.data.token);
        cardRef.current.classList.add("exit");
        setTimeout(() => nav("/pin"), 500);
      }
    } catch {
      setError("INVALID CREDENTIALS");
      triggerShake();
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") login();
  };

  return (
    <div className="atm-login-page">
      {/* Scanline overlay */}
      <div className="scanlines" />

      {/* Floating grid bg */}
      <div className="grid-bg" />

      {/* Corner accents */}
      <div className="corner tl" />
      <div className="corner tr" />
      <div className="corner bl" />
      <div className="corner br" />

      {/* Boot terminal */}
      <div className={`boot-terminal ${bootDone ? "hidden" : ""}`}>
        {TYPING_LINES.slice(0, bootLine).map((line, i) => (
          <div key={i} className="boot-line">
            <span className="boot-prompt">SYS &gt; </span>
            <span className="boot-text typewriter">{line}</span>
            {i === bootLine - 1 && <span className="boot-cursor" />}
          </div>
        ))}
      </div>

      {/* Login card */}
      {bootDone && (
        <div
          ref={cardRef}
          className={`atm-card ${shake ? "shake" : ""}`}
        >
          {/* Card header */}
          <div className="card-header">
            <div className="header-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="1" y="7" width="30" height="18" rx="3" stroke="#00ff88" strokeWidth="1.5" />
                <rect x="1" y="12" width="30" height="4" fill="#00ff88" opacity="0.25" />
                <rect x="5" y="18" width="8" height="2" rx="1" fill="#00ff88" opacity="0.6" />
                <circle cx="25" cy="19" r="3" stroke="#00ff88" strokeWidth="1.2" />
                <circle cx="28" cy="19" r="3" stroke="#00ff88" strokeWidth="1.2" opacity="0.5" />
              </svg>
            </div>
            <div>
              <div className="bank-name">NAPPS BANK</div>
              <div className="bank-sub">SECURE TERMINAL ACCESS</div>
            </div>
            <div className="header-dot" />
          </div>

          <div className="card-divider" />

          {/* Status bar */}
          <div className="status-bar">
            <span className="status-dot active" />
            <span className="status-text">SECURE CONNECTION ESTABLISHED</span>
            <span className="status-time">{new Date().toLocaleTimeString()}</span>
          </div>

          {/* Fields */}
          <div className="fields">
            <div className={`field-wrap ${focusField === "user" ? "focused" : ""}`}>
              <label className="field-label">
                <span className="label-bracket">[</span>
                USER ID
                <span className="label-bracket">]</span>
              </label>
              <div className="field-inner">
                <span className="field-prefix">&gt;_</span>
                <input
                  className="atm-input"
                  placeholder="Enter username"
                  value={username}
                  autoComplete="off"
                  onFocus={() => setFocusField("user")}
                  onBlur={() => setFocusField(null)}
                  onChange={(e) => setUsername(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <div className="field-underline" />
            </div>

            <div className={`field-wrap ${focusField === "pass" ? "focused" : ""}`}>
              <label className="field-label">
                <span className="label-bracket">[</span>
                PASSWORD
                <span className="label-bracket">]</span>
              </label>
              <div className="field-inner">
                <span className="field-prefix">&#x1F512;_</span>
                <input
                  className="atm-input"
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onFocus={() => setFocusField("pass")}
                  onBlur={() => setFocusField(null)}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <div className="field-underline" />
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="error-bar">
              <span className="error-icon">⚠</span>
              <span>{error}</span>
            </div>
          )}

          {/* Buttons */}
          <div className="btn-group">
            <button
              className={`atm-btn primary ${loading ? "loading" : ""}`}
              onClick={login}
              disabled={loading}
            >
              {loading ? (
                <span className="loading-dots">
                  <span />
                  <span />
                  <span />
                </span>
              ) : (
                <>
                  <span className="btn-arrow">▶</span>
                  AUTHENTICATE
                </>
              )}
            </button>

            <button
              className="atm-btn secondary"
              onClick={() => nav("/register")}
            >
              <span className="btn-plus">+</span>
              CREATE ACCOUNT
            </button>
          </div>

          {/* Footer */}
          <div className="card-footer">
            <span>256-BIT ENCRYPTED</span>
            <span className="footer-sep">|</span>
            <span>PCI DSS COMPLIANT</span>
            <span className="footer-sep">|</span>
            <span>ISO 27001</span>
          </div>
        </div>
      )}
    </div>
  );
}